import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { finalize, Observable, shareReplay } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class HttpRequestService {
  private readonly inFlightRequests = new Map<string, Observable<unknown>>();
  private header: HttpHeaders = new HttpHeaders({ responseType: 'JSON' });

  constructor(private _http: HttpClient) { }

  /**
   * Gets http request service
   * @param url 
   * @param [header] 
   * @returns get 
   */
  public get<T = object>(url: string, isWriteRequest?: boolean, header?: object): Observable<T> {
    const options = {
      headers: header === undefined ? this.header : new HttpHeaders(header as Record<string, string | string[]>),
    };

    if (isWriteRequest) {
      options.headers = options.headers.append('rfrw', 'true');
    }

    return this._http.get<T>(url, options);
  }

  /**
   * Posts http request service
   * @param [url] 
   * @param [body] 
   * @param [header] 
   * @returns post 
   */
  public post<T = object>(url: string, body: object, isWriteRequest?: boolean, header?: object): Observable<T> {
    const options = {
      headers: header === undefined ? this.header : new HttpHeaders(header as Record<string, string | string[]>),
    };

    if (isWriteRequest) {
      options.headers = options.headers.append('rfrw', 'true');
    }

    return this._http.post<T>(url, body, options);
  }


  public postBlob(url: string, body: object, isWriteRequest?: boolean, header?: object): Observable<Blob> {
    const options = {
      headers: header === undefined ? this.header : new HttpHeaders(header as Record<string, string | string[]>),
      responseType: 'blob' as const,
    };

    if (isWriteRequest) {
      options.headers = options.headers.append('rfrw', 'true');
    }

    const key = this.buildRequestKey('POST', url, body, isWriteRequest, header, 'blob');
    return this.dedupeInFlight(key, this._http.post(url, body, options), !isWriteRequest) as Observable<Blob>;
  }

  /**
   * Puts http request service
   * @param [url] 
   * @param [body] 
   * @param [header] 
   * @returns put 
   */
  public put<T = object>(url: string, body?: object, header?: object, isWriteRequest = true): Observable<T> {
    const options = {
      headers: header === undefined ? this.header : new HttpHeaders(header as Record<string, string | string[]>),
    };

    if (isWriteRequest) {
      options.headers = options.headers.append('rfrw', 'true');
    }

    return this._http.put<T>(url, body, options);
  }

  /**
   * Deletes http request service
   * @param [url] 
   * @param [header] 
   * @param [body] 
   * @returns delete 
   */
  public delete<T = object>(url: string, header?: object, body?: object, isWriteRequest = true): Observable<T> {
    const options = {
      headers: header === undefined ? this.header : new HttpHeaders(header as Record<string, string | string[]>),
      body: body,
    };

    if (isWriteRequest) {
      options.headers = options.headers.append('rfrw', 'true');
    }

    return this._http.request<T>('delete', url, options);
  }

  /**
  * Performs an HTTP PATCH request.
  * @param [url] The URL for the PATCH request.
  * @param [body] The body of the request.
  * @param [header] Optional headers to be sent with the request.
  * @returns Observable<Object> Observable containing the response from the server.
  */
  public patch<T = object>(url: string, body: object, header?: object, isWriteRequest = true): Observable<T> {
    const options = {
      headers: header === undefined ? this.header : new HttpHeaders(header as Record<string, string | string[]>),
    };

    if (isWriteRequest) {
      options.headers = options.headers.append('rfrw', 'true');
    }

    return this._http.patch<T>(url, body, options);
  }

  private buildRequestKey(method: string, url: string, body?: unknown, isWriteRequest?: boolean, header?: object, responseType?: string): string {
    return [
      method.toUpperCase(),
      url,
      responseType ?? 'json',
      isWriteRequest ? 'write' : 'read',
      this.stableStringify(header ?? {}),
      body === undefined ? '' : this.stableStringify(body),
    ].join('::');
  }

  private stableStringify(value: unknown): string {
    const seen = new WeakSet<object>();
    const replacer = (_key: string, val: any) => {
      if (!val || typeof val !== 'object') return val;

      if (seen.has(val)) return '[Circular]';
      seen.add(val);

      if (Array.isArray(val)) return val;

      const sorted: Record<string, unknown> = {};
      for (const key of Object.keys(val).sort()) {
        sorted[key] = val[key];
      }
      return sorted;
    };

    try {
      return JSON.stringify(value, replacer);
    } catch {
      return String(value);
    }
  }


  private dedupeInFlight<T>(key: string, source$: Observable<T>, enabled: boolean): Observable<T> {
    if (!enabled) return source$;

    const existing = this.inFlightRequests.get(key) as Observable<T> | undefined;
    if (existing) return existing;

    const shared$ = source$.pipe(
      shareReplay(1),
      finalize(() => this.inFlightRequests.delete(key)),
    );

    this.inFlightRequests.set(key, shared$ as Observable<unknown>);
    return shared$;
  }
}
